import React, { useEffect, useState } from 'react';
import { ScrollText, Loader2, Trash2, RefreshCw, Plus, Pencil, Ban } from 'lucide-react';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { fetchActivityLogs, ActivityLog } from '../../services/api/admin';

const ENTITY_LABEL: Record<string, string> = {
  course: 'Course',
  user: 'User',
  indicator_request: 'Indicator Access',
  copytrade: 'Join Copytrade',
};

const ACTION_META: Record<string, { label: string; icon: any; cls: string }> = {
  create: { label: 'Buat', icon: Plus, cls: 'text-emerald-300 bg-emerald-400/10 border-emerald-400/20' },
  update: { label: 'Ubah', icon: Pencil, cls: 'text-cyan-300 bg-cyan-400/10 border-cyan-400/20' },
  delete: { label: 'Hapus', icon: Trash2, cls: 'text-red-300 bg-red-400/10 border-red-400/20' },
  revoke: { label: 'Cabut', icon: Ban, cls: 'text-amber-300 bg-amber-400/10 border-amber-400/20' },
};

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('id-ID', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function AdminActivityPage() {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); fetchActivityLogs().then(setLogs).finally(() => setLoading(false)); };
  useEffect(load, []);

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Log Aktivitas">
      <div className="max-w-4xl" data-testid="admin-activity-page">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <ScrollText className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Audit</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Log Aktivitas Admin</h2>
            </div>
          </div>
          <button onClick={load} data-testid="activity-refresh" className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors" title="Muat ulang">
            <RefreshCw className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : logs.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada aktivitas tercatat.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => {
              const meta = ACTION_META[l.action] || ACTION_META.update;
              const Icon = meta.icon;
              return (
              <div key={l.id} data-testid="activity-row" className="flex items-center gap-3 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${meta.cls}`}>
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{l.description || `${l.action} ${l.entity_type}`}</p>
                  <p className="text-xs text-white/40 truncate">
                    Oleh <span className="text-white/60">{l.actor_name || l.actor_email}</span> · {formatWhen(l.created_at)}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${meta.cls}`}>{meta.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 px-2 py-0.5 rounded-full shrink-0">
                  {ENTITY_LABEL[l.entity_type] || l.entity_type}
                </span>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
